import * as models from './models'

export interface State {
  focused?: models.Values.Holder
  definitionsLookup: models.Definition.Lookup
  valuesLookup: models.Values.Lookup
  editingValuesLookup: models.Values.Lookup
}
export namespace State {
  export const INITIAL: Readonly<State> = {
    definitionsLookup: {},
    editingValuesLookup: {},
    valuesLookup: {},
  }

  /**
   * Sets the focused list of variables for a given {@link models.Values.Holder}
   */
  export function setFocused(
    state: State,
    holder: models.Values.Holder,
  ): State {
    return {
      ...state,
      focused: holder,
    }
  }

  /**
   * Removes the focused list of variables and cleans up any associated editing
   * state. If there's no focused holder, returns the state unchanged.
   */
  export function removeFocused(state: State): State {
    if (!state.focused) {
      return state
    }

    const holder = models.Values.Holder.stringify(state.focused)
    const { [holder]: _, ...remainingEditingValues } = state.editingValuesLookup

    return {
      ...state,
      editingValuesLookup: remainingEditingValues,
      focused: undefined,
    }
  }

  /**
   * Put the list of variable definitions for a given {@link models.Definition.Holder}
   */
  export function putDefinitions(
    state: State,
    holder: models.Definition.Holder,
    definitions: readonly models.Definition[],
  ): State {
    return {
      ...state,
      definitionsLookup: {
        ...state.definitionsLookup,
        [models.Definition.Holder.stringify(holder)]: definitions,
      },
    }
  }

  /**
   * Update variable value for a given {@link models.Values.Holder}
   */
  export function updateValue(
    state: State,
    { holder, variableId, value }: models.Values.UpdateRequest,
  ): State {
    const valuesHolderContext = models.Values.Holder.stringify(holder)

    return {
      ...state,
      editingValuesLookup: {
        ...state.editingValuesLookup,
        [valuesHolderContext]: {
          ...state.editingValuesLookup[valuesHolderContext],
          [variableId]: value,
        },
      },
    }
  }

  /**
   * Applies edited values to the values lookup for a given
   * {@link models.Values.Holder} and cleans up the editing state
   */
  export function applyValuesFor(
    state: State,
    holder: models.Values.Holder,
  ): State {
    const valuesHolderContext = models.Values.Holder.stringify(holder)
    const editingValues = state.editingValuesLookup[valuesHolderContext]

    // Create new valuesLookup with applied editing values
    const updatedValuesLookup = {
      ...state.valuesLookup,
      [valuesHolderContext]: {
        ...state.valuesLookup[valuesHolderContext],
        ...editingValues,
      },
    }

    // Remove the editing values entry
    const { [valuesHolderContext]: _, ...remainingEditingValues } =
      state.editingValuesLookup

    return {
      ...state,
      editingValuesLookup: remainingEditingValues,
      valuesLookup: updatedValuesLookup,
    }
  }

  /**
   * Put the variable values to the state for a given
   * {@link models.Values.Holder}
   */
  export function putValues(
    state: State,
    { holder, values }: models.Values.PutRequest,
  ): State {
    return {
      ...state,
      valuesLookup: {
        ...state.valuesLookup,
        [models.Values.Holder.stringify(holder)]: values,
      },
    }
  }

  /**
   * Reset variable values in the state for a given
   * {@link models.Values.Holder}
   */
  export function reset(state: State, holder: models.Values.Holder): State {
    const holderToRemove = models.Values.Holder.stringify(holder)
    const { [holderToRemove]: _activeValues, ...remainingValues } =
      state.valuesLookup
    const { [holderToRemove]: _editingValues, ...remainingEditingValues } =
      state.editingValuesLookup

    return {
      ...state,
      editingValuesLookup: remainingEditingValues,
      valuesLookup: remainingValues,
    }
  }

  /**
   * Add the variable values to the state for a given
   * {@link models.Values.Holder}
   *
   * TODO Consider having an option to add variable values to the state based on the specified behavior.
   * Defines how values should be added to the state:
   * - `"merge"`: Merges new values with the existing state, preserving all keys.
   * - `"overwrite-all"`: Replaces the state entirely with only the new values.
   * - `"overwrite-hidden"`: (TODO: Define the expected behavior for this option)
   *
   * @example
   * // Given the current state:
   * const state = {
   *   values: {
   *     "1": "abcd",
   *     "2": "def",
   *     "3": "def"
   *   }
   * };
   *
   * // And an action:
   * const action = {
   *   values: { "2": "abc" }
   * };
   *
   * // Behavior: "merge"
   * // Result:
   * // {
   * //   "1": "abcd",
   * //   "2": "abc",
   * //   "3": "def"
   * // }
   *
   * // Behavior: "overwrite-all"
   * // Result:
   * // {
   * //   "2": "abc"
   * // }
   */
  export function addValues(
    state: State,
    { holder, values }: models.Values.PutRequest,
  ): State {
    return {
      ...state,
      valuesLookup: {
        ...state.valuesLookup,
        [models.Values.Holder.stringify(holder)]: {
          ...state.valuesLookup[models.Values.Holder.stringify(holder)],
          ...values,
        },
      },
    }
  }
}
